import { useMemo } from 'react';

import { Box, Button, Flex, IconButton, Menu, MenuButton, MenuItem, MenuList, SystemProps } from '@chakra-ui/react';
import { focusStyles } from '@udacity/chakra-uds-theme';

import { useI18n } from '~/hooks/use-i18n';
import { isSelectOption } from '~/types/select-option';

import { ChevronLeftIcon, ChevronRightIcon } from '../icons';
import { DEFAULT_MAX_VISIBLE_PAGES, getDisplayedPageNumbers } from '../utils/get-displayed-page-numbers';

const DEFAULT_SPACING = '8px';

export function Pagination({
  currentPage,
  totalPages,
  pageIsZeroIndexed = false,
  maxVisiblePages = DEFAULT_MAX_VISIBLE_PAGES,
  spacing = DEFAULT_SPACING,
  onChange,
}: {
  currentPage: number;
  totalPages: number;
  pageIsZeroIndexed?: boolean;
  /**
   * Page buttons in the sliding window. Lower it where the row has to fit a narrow container: at 3
   * the widest arrangement is seven controls instead of eleven.
   */
  maxVisiblePages?: number;
  /** Space between controls. The arrows take a second helping to set them apart from the numbers. */
  spacing?: SystemProps['gap'];
  onChange: (page: number) => void;
}) {
  const { isRtl } = useI18n();
  const linkFocusStyles = focusStyles('primary.blue');
  const options = Array.from({ length: totalPages }, (_, i) => ({
    label: `Page ${i + 1}`,
    value: `${i + 1}`,
  }));

  const displayedCurrentPage = pageIsZeroIndexed ? currentPage + 1 : currentPage;

  const displayedPageNumbers = useMemo(
    () => getDisplayedPageNumbers({ currentPage, totalPages, maxVisiblePages, pageIsZeroIndexed }),
    [currentPage, maxVisiblePages, pageIsZeroIndexed, totalPages]
  );

  function handlePageChange(newValue: unknown) {
    if (isSelectOption(newValue)) {
      onChange(pageIsZeroIndexed ? Number(newValue.value) - 1 : Number(newValue.value));
    }
  }

  const shouldDisplayFirstPageNumber = displayedPageNumbers[0] > 1;
  const shouldDisplayLastPageNumber = !displayedPageNumbers.includes(totalPages) && totalPages > 0;

  const PagesMenu = (
    <Menu>
      <MenuButton
        aria-label='Select a page of results'
        {...(linkFocusStyles as any)}
        borderRadius='2px'
        color='black'
        height='24px'
        minWidth='24px'
        paddingX='4px'
        transition='all 0.3s ease'
        _focusVisible={{
          ...linkFocusStyles._focusVisible,

          _before: {
            ...linkFocusStyles._focusVisible._before,

            width: '100%',
            height: '100%',
          },
        }}
        _hover={{
          color: 'primary.blue',
          background: 'supportBlue.10',
        }}
      >
        ...
      </MenuButton>
      <MenuList maxHeight='300px' overflow='auto' zIndex='99'>
        {options.map((option) => (
          <MenuItem key={option.value} onClick={() => handlePageChange(option)}>
            {option.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );

  if (totalPages === 0) {
    return null;
  }

  return (
    <Flex alignItems='center' flex={1} gap={spacing} justifyContent='center'>
      <IconButton
        aria-label='Show previous page of results'
        flexShrink={0}
        icon={isRtl ? <ChevronRightIcon h='20px' w='20px' /> : <ChevronLeftIcon h='20px' w='20px' />}
        isDisabled={displayedCurrentPage === 1}
        marginRight={spacing}
        size='sm'
        variant='outline'
        onClick={() => onChange(currentPage - 1)}
      />

      {totalPages > 0 && (
        <>
          {shouldDisplayFirstPageNumber && (
            <>
              <PaginationButton
                currentPage={currentPage}
                pageIsZeroIndexed={pageIsZeroIndexed}
                pageNumber={1}
                onChange={onChange}
              />

              {PagesMenu}
            </>
          )}

          <Box display='flex' gap={spacing}>
            {displayedPageNumbers.map((pageNumber) => (
              <PaginationButton
                key={pageNumber}
                currentPage={currentPage}
                pageIsZeroIndexed={pageIsZeroIndexed}
                pageNumber={pageNumber}
                onChange={onChange}
              />
            ))}
          </Box>

          {shouldDisplayLastPageNumber && (
            <>
              {PagesMenu}

              <PaginationButton
                currentPage={currentPage}
                pageIsZeroIndexed={pageIsZeroIndexed}
                pageNumber={totalPages}
                onChange={onChange}
              />
            </>
          )}
        </>
      )}

      <IconButton
        aria-label='Show next page of results'
        flexShrink={0}
        icon={isRtl ? <ChevronLeftIcon h='20px' w='20px' /> : <ChevronRightIcon h='20px' w='20px' />}
        isDisabled={displayedCurrentPage === totalPages}
        marginLeft={spacing}
        size='sm'
        variant='outline'
        onClick={() => onChange(currentPage + 1)}
      />
    </Flex>
  );
}

function PaginationButton({
  pageNumber,
  currentPage,
  pageIsZeroIndexed,
  onChange,
}: {
  pageNumber: number;
  currentPage: number;
  pageIsZeroIndexed?: boolean;
  onChange: (page: number) => void;
}) {
  const displayedCurrentPage = pageIsZeroIndexed ? currentPage + 1 : currentPage;
  const isCurrentPage = displayedCurrentPage === pageNumber;

  const handleClick = () => {
    const targetPage = pageIsZeroIndexed ? pageNumber - 1 : pageNumber;
    onChange(targetPage);
  };

  return (
    <Button
      key={pageNumber}
      size='xs'
      sx={isCurrentPage ? { bgColor: 'primary.blue', color: 'white !important' } : {}}
      variant='outline'
      onClick={handleClick}
    >
      {pageNumber}
    </Button>
  );
}
